import { useEffect, useState } from "react"

export const RELEASE_CHECK_INTERVAL_MS = 5 * 60 * 1000
const MIN_CHECK_GAP_MS = 30_000
const CHECK_TIMEOUT_MS = 10_000

/** Static Hosting metadata only: no Firebase calls, session IDs or user data. */
export function useReleaseUpdate(currentVersion: string, versionUrl: string) {
  const [available, setAvailable] = useState(false)
  useEffect(() => {
    let disposed = false
    let pending = false
    let lastCheck = -Infinity
    let controller: AbortController | undefined
    let timeout: ReturnType<typeof setTimeout> | undefined
    setAvailable(false)
    const check = async () => {
      if (disposed || pending || document.visibilityState === "hidden" || navigator.onLine === false || Date.now() - lastCheck < MIN_CHECK_GAP_MS) return
      pending = true
      lastCheck = Date.now()
      controller = new AbortController()
      timeout = setTimeout(() => controller?.abort(), CHECK_TIMEOUT_MS)
      try {
        const response = await fetch(versionUrl, {
          cache: "no-store", headers: { Accept: "application/json" }, signal: controller.signal,
        })
        if (!response.ok || !response.headers.get("content-type")?.includes("application/json")) return
        const data: unknown = await response.json()
        if (disposed || !data || typeof data !== "object" || !("version" in data) ||
          typeof data.version !== "string" || !/^[A-Za-z0-9._-]{1,128}$/.test(data.version)) return
        // A rollback to the tab's build also clears an obsolete notification.
        setAvailable(data.version !== currentVersion)
      } catch {
        // Offline, missing manifests and transient failures never reload or
        // block the app. Preserve any already-confirmed update notification.
      } finally {
        clearTimeout(timeout)
        pending = false
      }
    }
    const onReturn = () => { void check() }
    const interval = setInterval(onReturn, RELEASE_CHECK_INTERVAL_MS)
    document.addEventListener("visibilitychange", onReturn)
    window.addEventListener("focus", onReturn)
    window.addEventListener("online", onReturn)
    void check()
    return () => {
      disposed = true
      clearInterval(interval)
      clearTimeout(timeout)
      controller?.abort()
      document.removeEventListener("visibilitychange", onReturn)
      window.removeEventListener("focus", onReturn)
      window.removeEventListener("online", onReturn)
    }
  }, [currentVersion, versionUrl])
  return available
}
